import api from "@/api";
import recurly from "@/services/recurly";
import { trackEvent } from "@/services/tracking/hardened";
import { go } from "@/utils/location";
import cookie, { eraseCookie } from "@/utils/cookie";
import { isEmailInvalid } from "@/modules/email-validation";

export default {
  config: ({ commit, getters }, {
    productType,
    plan,
    coupon,
    countryCode,
    tosPath,
    trialDays,
    defaultPaymentMethod,
    countryOptions,
    isIosAppPurchase,
    isInVariation
  }) => {
    commit("config", {
      productType,
      countryCode,
      tosPath,
      trialDays,
      countryOptions,
      isIosAppPurchase,
      isInVariation
    });
    commit("plan", plan);
    commit("coupon", coupon);

    let selectedPaymentMethod = (
      getters.paymentMethods[defaultPaymentMethod] ?
        defaultPaymentMethod :
        Object.keys(getters.paymentMethods)[0]
    );

    commit("selectedPaymentMethod", selectedPaymentMethod);
  },

  fetchPlan: ({ dispatch, commit, state }) => {
    commit("loading", true);
    return api.plans({
      zip: state.zip.value,
      countryCode: state.countryCode,
      code: state.coupon.code,
      sku: state.plan.sku,
      token: state.plan.token,
      productType: state.productType
    })
      .then((data) => {
        commit("loading", false);
        dispatch("saveCoupon", data.coupon);
        commit("tax", data.tax);
        commit("plan", data.plan);
      });
  },

  validate: ({ state, commit, getters }) => new Promise((resolve, reject) => {
    commit("zip", {
      ...state.zip,
      error: false
    });

    const errorFields = [];

    if (getters.isCountryUS && (!state.zip.value || state.zip.value === "")) {
      commit("zip", {
        ...state.zip,
        error: true
      });
      errorFields.push("zip");
    }

    if (state.productType === "giftcards" && state.giftcard.receiver.type === "giftee") {
      if (!state.giftcard.recipientName) {
        errorFields.push("recipient_name");
      }

      if (isEmailInvalid(state.giftcard.receiver.email)) {
        errorFields.push("email");
      }

      if (!state.giftcard.message) {
        errorFields.push("message");
      }
    }

    if (errorFields.length > 0) {
      return reject({ fields: errorFields });
    }

    resolve();
  }),

  submit: ({ commit, state }, payload) => {
    commit("error", null);

    let params = {
      ...payload,
      "subscription_plan_token": state.plan.token,
      "payment_type": state.selectedPaymentMethod,
      "country": state.countryCode,
      "postal_code": state.zip.value,
      "coupon_code": state.coupon.code,
      currency: state.plan.currency
    };

    if (state.productType === "giftcards") {
      params.receiver = state.giftcard.receiver.type;
      params.recipient_name = state.giftcard.recipientName; /* eslint-disable-line camelcase */
      params.recipient_email = state.giftcard.receiver.email; /* eslint-disable-line camelcase */
      params.message = state.giftcard.message;
    }

    return api.purchase(params, state.productType)
      .then((data) => {
        if (data.message === "3d_secure_action_required") {
          commit("showThreeDs", true);
          const threeDsContainer = document.querySelector(".js-recurly-3ds");
          trackEvent({
            category: "body-20181129-payment_form",
            name: "show",
            action: "3ds"
          });
          return recurly.threeds.challenge(data.token, threeDsContainer)
            .then(token => {
              trackEvent({
                category: "body-20181129-payment_form",
                name: "token",
                action: "3ds"
              });

              let params = {
                ...payload,
                "subscription_plan_token": state.plan.token,
                "payment_type": state.selectedPaymentMethod,
                "country": state.countryCode,
                "postal_code": state.zip.value,
                "coupon_code": state.coupon.code,
                "three_ds_token": token.id,
                currency: state.plan.currency
              };

              if (state.productType === "giftcards") {
                params.receiver = state.giftcard.receiver.type;
                params.recipient_name = state.giftcard.recipientName; /* eslint-disable-line camelcase */
                params.recipient_email = state.giftcard.receiver.email; /* eslint-disable-line camelcase */
                params.message = state.giftcard.message;
              }

              return api.purchase({
                ...params
              }, state.productType);
            });
        } else {
          return Promise.resolve(data);
        }
      })
      .then((data) => {
        const { successPath } = data;
        eraseCookie("coupon_code");
        go(successPath);
      })
      .catch(err => {
        let message = err.content && err.content.message || err.message;
        commit("error", message);

        trackEvent({
          category: "body-20181129-payment_form",
          action: "blinkist-error",
          customVariables: {
            Message: message
          }
        });
      })
      .finally(() => {
        commit("showThreeDs", false);
      });
  },

  submitCoupon: ({ commit, dispatch }, couponCode) => {
    commit("coupon", { code: couponCode, loading: true });
    trackEvent({
      category: "form-20210120-checkout_coupon",
      action: "1-submit",
      name: couponCode
    });
    return dispatch("fetchPlan")
      .finally(() => {
        commit("coupon", { loading: false });
      });
  },

  submitZip: ({ commit, dispatch }) => {
    commit("zip", { loading: true });
    return dispatch("fetchPlan")
      .then(() => {
        commit("zip", { error: false });
      })
      .catch(() => {
        commit("zip", { error: true });
        trackEvent({
          category: "body-20201125-payment_form",
          action: "vat_checkout_summary_error"
        });
      })
      .finally(() => {
        commit("zip", { loading: false });
      });
  },

  country: ({ commit, dispatch, state }, countryCode) => {
    commit("countryCode", countryCode);
    trackEvent({
      category: "body-20210219-payment_form",
      action: "change_country",
      name: countryCode
    });
    if (countryCode !== "us" && state.tax.amount) {
      commit("zip", { value: null });
      dispatch("submitZip");
    }
  },

  saveCoupon: ({ commit, state }, payload) => {
    if (!payload) {
      commit("coupon", {
        type: null,
        value: null,
        code: null,
        error: state.coupon.loading
      });
      return;
    }
    commit("coupon", payload);
    cookie("coupon_code", payload.code, 1);
  },

  resetCoupon: ({ commit, dispatch }) => {
    commit("coupon", { code: null, type: null, value: null, error: null });
    eraseCookie("coupon_code");
    dispatch("fetchPlan");
  },

  sending: ({ commit }, payload) => {
    commit("loading", payload);
    commit("sending", payload);
  },

  selectedPaymentMethod: ({ commit }, payload) => {
    commit("selectedPaymentMethod", payload);
    trackEvent({
      category: "body-20181129-payment_form",
      name: "select",
      action: payload
    });
  },

  updateGiftcardReceiver: ({ commit }, payload) => {
    commit("giftcard", payload);
  },

  updateGiftcardMessage: ({ commit }, payload) => {
    commit("giftcard", payload);
  }
};
